// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
import type { AppContext } from '../app';
import type { Facility } from '../types';
import { el, icon, clear } from '../dom';
import { formatMass, formatYear } from '../format';
import { divisionColor, stateColor, MEDIUM_COLORS } from '../colors';

export function openFacility(id: number, ctx: AppContext) {
  const facility = ctx.facilitiesById.get(id);
  if (!facility) return;
  const panel = ensurePanel();
  clear(panel);
  panel.appendChild(buildContent(facility, ctx));
  requestAnimationFrame(() => panel.classList.add('open'));
  document.body.style.overflow = 'hidden';
}

function ensurePanel(): HTMLElement {
  let panel = document.querySelector<HTMLElement>('.detail-panel');
  if (panel) return panel;
  panel = document.createElement('aside');
  panel.className = 'detail-panel';
  document.body.appendChild(panel);
  document.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape' && panel!.classList.contains('open')) closePanel();
  });
  return panel;
}

function closePanel() {
  const panel = document.querySelector<HTMLElement>('.detail-panel');
  if (!panel) return;
  panel.classList.remove('open');
  document.body.style.overflow = '';
}

function buildContent(f: Facility, ctx: AppContext): HTMLElement {
  const transfers = ctx.transfersById.get(f.id);
  const ns = el('div');
  ns.appendChild(el('button', { class: 'detail-close', 'aria-label': 'Close', onclick: closePanel }, [icon('close', 18)]));

  ns.appendChild(el('div', { class: 'detail-head' }, [
    el('h2', null, f.name),
    el('div', { class: 'sub' }, [
      el('span', {
        class: 'pill pill-state',
        style: `background:${stateColor(f.state)}22;color:${stateColor(f.state)};border-color:${stateColor(f.state)}40`
      }, f.state || '—'),
      ' ',
      f.suburb || '—',
      f.postcode ? ` · ${f.postcode}` : ''
    ]),
    el('div', { class: 'detail-meta' }, [
      meta('Business', f.business_name || '—'),
      meta('ABN', f.abn || '—'),
      meta('Industry (ANZSIC)', f.anzsic_class_name),
      meta('Division', el('span', null, [
        el('span', { class: 'legend-swatch', style: `background:${divisionColor(f.division_code)}` }),
        f.division_name || '—'
      ])),
      meta('Reporting span', [formatYear(f.first_year), '–', formatYear(f.latest_year)].join('')),
      meta('Reports filed', String(f.report_count))
    ])
  ]));

  // Totals row
  const totals = el('div', { class: 'kpi-row' });
  totals.appendChild(kpi('Air (lifetime)', formatMass(f.air), MEDIUM_COLORS.air));
  totals.appendChild(kpi('Water (lifetime)', formatMass(f.water), MEDIUM_COLORS.water));
  totals.appendChild(kpi('Land (lifetime)', formatMass(f.land), MEDIUM_COLORS.land));
  totals.appendChild(kpi('Latest year total', formatMass(f.latest_total)));
  ns.appendChild(totals);

  // Year-by-year mini chart
  const historyEntries = Object.entries(f.history)
    .map(([y, v]) => ({ year: Number(y), ...v }))
    .sort((a, b) => a.year - b.year);
  if (historyEntries.length > 1) {
    const hcard = el('div', { class: 'card mb-lg' });
    hcard.appendChild(el('h3', { class: 'card-title' }, 'Recent year-by-year history'));
    hcard.appendChild(el('p', { class: 'card-subtitle' }, 'Stacked bars by medium. Latest 15 reporting years.'));
    hcard.appendChild(buildHistoryChart(historyEntries));
    ns.appendChild(hcard);
  }

  // Top substances
  if (f.top_substances.length > 0) {
    const sCard = el('div', { class: 'card mb-lg' });
    sCard.appendChild(el('h3', { class: 'card-title' }, 'Top substances released'));
    sCard.appendChild(el('p', { class: 'card-subtitle' }, 'By lifetime total. Click any substance to open its national view.'));
    const max = f.top_substances[0].kg;
    sCard.appendChild(el('div', { style: 'display: flex; flex-direction: column; gap: var(--space-sm);' },
      f.top_substances.map(t => {
        const sub = ctx.substancesById.get(t.id);
        const name = sub?.name ?? `Substance #${t.id}`;
        return el('div', null, [
          el('div', { style: 'display: flex; justify-content: space-between; font-size: var(--font-size-sm); margin-bottom: 4px' }, [
            sub?.fact_sheet_url
              ? el('a', { href: sub.fact_sheet_url, target: '_blank', rel: 'noopener' }, name)
              : el('span', null, name),
            el('span', { class: 'text-mono text-secondary' }, formatMass(t.kg))
          ]),
          el('div', { style: 'background: var(--bg-elevated); border-radius: 4px; height: 8px; overflow: hidden;' }, [
            el('div', { style: `background: ${divisionColor(f.division_code)}; height: 100%; width: ${(t.kg / max) * 100}%;` })
          ])
        ]);
      })
    ));
    ns.appendChild(sCard);
  }

  // Transfers
  if (transfers && transfers.destinations.length > 0) {
    const tCard = el('div', { class: 'card mb-lg' });
    tCard.appendChild(el('h3', { class: 'card-title' }, [
      'Off-site ',
      el('span', { class: 'glossary-link', 'data-term': 'transfer', tabindex: '0' }, 'transfers')
    ]));
    tCard.appendChild(el('p', { class: 'card-subtitle' }, 'Substances that left the site instead of being released — landfill, sewer, off-site reprocessing or reuse.'));
    const maxT = transfers.destinations[0].kg;
    tCard.appendChild(el('div', { style: 'display: flex; flex-direction: column; gap: var(--space-sm);' },
      transfers.destinations.map(d => el('div', null, [
        el('div', { style: 'display: flex; justify-content: space-between; font-size: var(--font-size-sm); margin-bottom: 4px' }, [
          el('span', null, d.dest),
          el('span', { class: 'text-mono text-secondary' }, formatMass(d.kg))
        ]),
        el('div', { style: 'background: var(--bg-elevated); border-radius: 4px; height: 6px; overflow: hidden;' }, [
          el('div', { style: `background: var(--accent-warm); height: 100%; width: ${(d.kg / maxT) * 100}%;` })
        ])
      ]))
    ));
    ns.appendChild(tCard);
  }

  // Source link
  ns.appendChild(el('div', { class: 'text-tertiary', style: 'font-size: var(--font-size-xs); margin-top: var(--space-lg)' },
    `Source: National Pollutant Inventory. Verify any numbers used in reporting against the official record.`));

  return ns;
}

function meta(label: string, value: string | HTMLElement): HTMLElement {
  return el('div', null, [
    el('div', { class: 'k' }, label),
    el('div', { class: 'v' }, value as never)
  ]);
}

function kpi(label: string, value: string, color?: string): HTMLElement {
  return el('div', { class: 'kpi' }, [
    el('div', { class: 'kpi-label' }, label),
    el('div', { class: 'kpi-value', style: color ? `color:${color}` : '' }, value)
  ]);
}

function buildHistoryChart(rows: Array<{ year: number; a: number; w: number; l: number }>): HTMLElement {
  const W = 500, H = 200, padL = 50, padR = 12, padT = 12, padB = 28;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const totals = rows.map(r => r.a + r.w + r.l);
  const max = Math.max(1, ...totals);
  const ns = 'http://www.w3.org/2000/svg';
  const svgEl = document.createElementNS(ns, 'svg');
  svgEl.setAttribute('viewBox', `0 0 ${W} ${H}`);
  svgEl.setAttribute('class', 'chart');
  svgEl.setAttribute('preserveAspectRatio', 'xMidYMid meet');

  for (let i = 0; i <= 4; i++) {
    const t = i / 4;
    const y = padT + innerH * (1 - t);
    const line = document.createElementNS(ns, 'line');
    line.setAttribute('x1', String(padL));
    line.setAttribute('x2', String(W - padR));
    line.setAttribute('y1', String(y));
    line.setAttribute('y2', String(y));
    line.setAttribute('stroke', 'var(--border-subtle)');
    line.setAttribute('stroke-dasharray', i === 0 ? '0' : '2,3');
    svgEl.appendChild(line);
    const lab = document.createElementNS(ns, 'text');
    lab.setAttribute('x', String(padL - 4));
    lab.setAttribute('y', String(y + 3));
    lab.setAttribute('text-anchor', 'end');
    lab.setAttribute('font-size', '9');
    lab.setAttribute('fill', 'var(--text-tertiary)');
    lab.textContent = formatMass(max * t);
    svgEl.appendChild(lab);
  }

  const barW = innerW / rows.length;
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    const segs = [
      { v: r.l, color: MEDIUM_COLORS.land },
      { v: r.w, color: MEDIUM_COLORS.water },
      { v: r.a, color: MEDIUM_COLORS.air }
    ];
    let cum = 0;
    const xBase = padL + i * barW + 1;
    for (const s of segs) {
      const h = (s.v / max) * innerH;
      const y = padT + innerH - cum - h;
      cum += h;
      const rect = document.createElementNS(ns, 'rect');
      rect.setAttribute('x', String(xBase));
      rect.setAttribute('y', String(y));
      rect.setAttribute('width', String(Math.max(2, barW - 3)));
      rect.setAttribute('height', String(h));
      rect.setAttribute('fill', s.color);
      const tip = `${formatYear(r.year)}: ${formatMass(s.v)}`;
      rect.setAttribute('data-tip', tip);
      rect.setAttribute('aria-label', tip);
      svgEl.appendChild(rect);
    }
    if (i % 2 === 0) {
      const lab = document.createElementNS(ns, 'text');
      lab.setAttribute('x', String(xBase + barW / 2 - 1));
      lab.setAttribute('y', String(H - padB + 14));
      lab.setAttribute('text-anchor', 'middle');
      lab.setAttribute('font-size', '9');
      lab.setAttribute('fill', 'var(--text-tertiary)');
      lab.textContent = String(r.year);
      svgEl.appendChild(lab);
    }
  }
  const wrap = document.createElement('div');
  wrap.appendChild(svgEl);
  return wrap;
}
